import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  Copy,
  ExternalLink,
  MoreVertical,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCancelBoleto, useDeleteBoleto } from './useBoletos';
import { BOLETO_STATUS_BADGE, BOLETO_STATUS_LABELS, effectiveStatus } from './types';
import type { BillingInvoice } from './types';

function formatBrl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

interface Props {
  boleto: BillingInvoice;
  onEdit: () => void;
  onMarkPaid: () => void;
}

export function BoletoRow({ boleto, onEdit, onMarkPaid }: Props) {
  const cancel = useCancelBoleto();
  const remove = useDeleteBoleto();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const status = effectiveStatus(boleto);
  const canPay = status === 'pending' || status === 'overdue';

  const copyBarCode = () => {
    if (!boleto.bar_code) return;
    navigator.clipboard.writeText(boleto.bar_code);
    toast.success('Código de barras copiado.');
  };

  return (
    <>
      <tr className="border-b border-border/50 hover:bg-secondary/20">
        <td className="px-3 py-2 text-xs">
          <p className="font-medium text-foreground">{boleto.payee_name}</p>
          {boleto.payee_document && (
            <p className="text-muted-foreground">{boleto.payee_document}</p>
          )}
        </td>
        <td className="px-3 py-2 text-xs text-foreground">{boleto.description}</td>
        <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums">
          {formatBrl(Number(boleto.amount))}
        </td>
        <td className="px-3 py-2 text-xs">
          {format(new Date(`${boleto.due_date}T00:00:00`), 'dd/MM/yyyy', { locale: ptBR })}
        </td>
        <td className="px-3 py-2">
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${BOLETO_STATUS_BADGE[status]}`}
          >
            {BOLETO_STATUS_LABELS[status]}
          </span>
          {boleto.status === 'paid' && boleto.paid_at && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {format(new Date(boleto.paid_at), 'dd/MM/yyyy', { locale: ptBR })}
            </p>
          )}
        </td>
        <td className="px-3 py-2 text-right">
          <div className="flex items-center justify-end gap-1">
            {canPay && (
              <Button size="sm" variant="income" onClick={onMarkPaid} className="h-7 px-2 text-xs">
                <CheckCircle2 size={11} className="mr-1" />
                Pagar
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="ghost" className="h-7 w-7">
                  <MoreVertical size={13} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {boleto.bar_code && (
                  <DropdownMenuItem onClick={copyBarCode}>
                    <Copy size={13} className="mr-2" />
                    Copiar código de barras
                  </DropdownMenuItem>
                )}
                {boleto.boleto_url && (
                  <DropdownMenuItem
                    onClick={() => window.open(boleto.boleto_url!, '_blank', 'noreferrer')}
                  >
                    <ExternalLink size={13} className="mr-2" />
                    Abrir PDF do boleto
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={onEdit} disabled={status === 'paid'}>
                  <Pencil size={13} className="mr-2" />
                  Editar
                </DropdownMenuItem>
                {canPay && (
                  <DropdownMenuItem
                    onClick={() => setConfirmCancel(true)}
                    className="text-amber-600 focus:text-amber-600"
                  >
                    <XCircle size={13} className="mr-2" />
                    Cancelar boleto
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => setConfirmDelete(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 size={13} className="mr-2" />
                  Excluir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </td>
      </tr>

      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar boleto?</AlertDialogTitle>
            <AlertDialogDescription>
              O boleto será marcado como cancelado e a transação a receber vinculada será removida.
              O registro do boleto fica preservado para histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => cancel.mutate(boleto)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancelar boleto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir boleto?</AlertDialogTitle>
            <AlertDialogDescription>
              {status === 'paid'
                ? 'O boleto será excluído mas a transação de receita já paga é mantida no histórico.'
                : 'Boleto e transação a receber serão removidos.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove.mutate(boleto)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
